import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject, of } from "rxjs";
import {
  catchError,
  debounceTime,
  delay,
  switchMap,
  tap,
} from "rxjs/operators";
import { HttpClient } from "@angular/common/http";
import { User } from "../models/user.model";
import {
  SortColumn,
  SortDirection,
} from "../pages/users/components/user-list/user-list-sortable.directive";
import { environment } from "src/environments/environment";
import { UserProfile } from "../models/user-profile.model";

interface SearchResult {
  usuarios: User[];
}

interface State {
  page: number;
  pageSize: number;
  searchTerm: string;
  sortColumn: SortColumn;
  sortDirection: SortDirection;
}

@Injectable({
  providedIn: "root",
})
export class UserService {
  private _apiUrl = environment.apiUrl + "/v1/usuarios";
  private _loading$ = new BehaviorSubject<boolean>(true);
  private _search$ = new Subject<void>();
  private _rows$ = new BehaviorSubject<User[]>([]);
  private _total$ = new BehaviorSubject<number>(0);
  private _allUsers$ = new BehaviorSubject<User[]>([]); // BehaviorSubject for all users
  private _state: State = {
    page: 1,
    pageSize: 10,
    searchTerm: "",
    sortColumn: "",
    sortDirection: "",
  };

  constructor(private http: HttpClient) {
    this._search$
      .pipe(
        tap(() => this._loading$.next(true)),
        debounceTime(200),
        switchMap(() => this._search()),
        delay(200),
        tap(() => this._loading$.next(false)),
      )
      .subscribe((result) => {
        this._rows$.next(result.usuarios);
      });
  }

  get rows$(): Observable<User[]> {
    return this._rows$.asObservable();
  }

  get total$(): Observable<number> {
    return this._total$.asObservable();
  }

  get loading$(): Observable<boolean> {
    return this._loading$.asObservable();
  }

  get allUsers$(): Observable<User[]> {
    // Expose all users as an observable
    return this._allUsers$.asObservable();
  }

  set page(page: number) {
    this._set({ page });
  }

  set pageSize(pageSize: number) {
    this._set({ pageSize });
  }

  set searchTerm(searchTerm: string) {
    this._set({ searchTerm });
  }

  set sortColumn(sortColumn: SortColumn) {
    this._set({ sortColumn });
  }

  set sortDirection(sortDirection: SortDirection) {
    this._set({ sortDirection });
  }

  get page(): number {
    return this._state.page;
  }

  get pageSize(): number {
    return this._state.pageSize;
  }

  get lastPage(): number {
    return Math.ceil(this._total$.value / this.pageSize) || 1; // Ensure at least 1
  }

  private _set(patch: Partial<State>): void {
    Object.assign(this._state, patch);
    this._search$.next();
  }

  // Fetch all users and store in allUsers
  fetchAllUsers(): Observable<SearchResult> {
    return this.http.get<SearchResult>(this._apiUrl).pipe(
      tap((result) => {
        this._allUsers$.next(result.usuarios); // Update BehaviorSubject
        this._total$.next(result.usuarios.length);

        // Explicitly update _rows$ to trigger table refresh
        this._rows$.next(result.usuarios);

        this._search$.next(); // Trigger search to refresh displayed rows
      }),
      catchError((error) => {
        console.error("Error fetching users:", error);
        return of({ usuarios: [] }); // Return empty array on error
      }),
    );
  }

  private _search(): Observable<SearchResult> {
    const { sortColumn, sortDirection, pageSize, page, searchTerm } =
      this._state;

    // Start with all users
    let filteredRows = this._allUsers$.value; // Use the BehaviorSubject's current value

    // Filter the data
    if (searchTerm) {
      filteredRows = filteredRows.filter((user) =>
        Object.values(user).some(
          (value) =>
            typeof value === "string" &&
            value.toLowerCase().includes(searchTerm.toLowerCase()),
        ),
      );
    }

    // Sort the data
    if (sortColumn && sortDirection) {
      filteredRows = filteredRows.sort((a, b) => {
        const aValue = a[sortColumn] ?? ""; // Handle undefined values
        const bValue = b[sortColumn] ?? "";

        if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
        if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
    }

    // Paginate the data
    const start = (page - 1) * pageSize;
    // Recalculate the pages
    const rows = filteredRows.slice(start, start + pageSize);
    const total = filteredRows.length;

    return of({ usuarios: rows, total }); // Correctly return the total
  }

  /**
   * Create a new user
   * @param user The user data to create
   */
  createUser(user: Omit<User, "habilitado">): Observable<User> {
    return this.http.post<User>(this._apiUrl, user);
  }

  /**
   * Edit an existing user
   * @param user The user data to update
   */
  editUser(user: User): Observable<User> {
    return this.http.put<User>(`${this._apiUrl}/${user.id}`, user);
  }

  getUserById(id: string): Observable<UserProfile> {
    return this.http.get<UserProfile>(`${this._apiUrl}/${id}`);
  }

  updateUserRoles(user: UserProfile): Observable<UserProfile> {
    return this.http.put<UserProfile>(
      `${this._apiUrl}/${user.id}/permissoes`,
      user,
    );
  }
}
